import { Description, Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import {
    cloneElement,
    createContext,
    forwardRef,
    Fragment,
    type HTMLAttributes,
    isValidElement,
    type ReactElement,
    type ReactNode,
    useContext,
    useState
} from 'react';

interface SheetContextValue {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

const SheetContext = createContext<SheetContextValue | null>(null);

interface SheetProps {
    children?: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function Sheet({ children, open, onOpenChange }: SheetProps) {
    const [
        internalOpen,
        setInternalOpen,
    ] = useState(false);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? Boolean(open) : internalOpen;
    const setIsOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;

    return (
        <SheetContext.Provider value={ { isOpen, setIsOpen } }>
            {children}
        </SheetContext.Provider>
    );
}

function useSheetContext(): SheetContextValue {
    const context = useContext(SheetContext);

    if (!context) {
        throw new Error('Sheet components must be used within a Sheet');
    }
    return context;
}

interface ClonableElementProps {
    onClick?: (e: React.MouseEvent) => void;
    ref?: React.Ref<HTMLElement>;
}

interface SheetTriggerProps extends HTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
    children?: ReactNode;
}

const SheetTrigger = forwardRef<HTMLButtonElement, SheetTriggerProps>(({ children, asChild, className, ...props }, ref) => {
    const { setIsOpen } = useSheetContext();

    if (asChild && isValidElement(children)) {
        const child = children as ReactElement<ClonableElementProps>;

        // eslint-disable-next-line react-hooks/refs -- forwarding the ref onto the cloned child, not reading its .current
        return cloneElement(child, {
            ...props,
            ref,
            onClick: (e: React.MouseEvent) => {
                child.props.onClick?.(e);
                setIsOpen(true);
            },
        });
    }

    return (
        <button
            className={ className }
            onClick={ () => {
                return setIsOpen(true);
            } }
            ref={ ref }
            type='button'
            { ...props }
        >
            {children}
        </button>
    );
});

SheetTrigger.displayName = 'SheetTrigger';

interface SheetCloseProps extends HTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
    children?: ReactNode;
}

const SheetClose = forwardRef<HTMLButtonElement, SheetCloseProps>(({ children, asChild, className, ...props }, ref) => {
    const { setIsOpen } = useSheetContext();

    if (asChild && isValidElement(children)) {
        const child = children as ReactElement<ClonableElementProps>;

        // eslint-disable-next-line react-hooks/refs -- forwarding the ref onto the cloned child, not reading its .current
        return cloneElement(child, {
            ...props,
            ref,
            onClick: (e: React.MouseEvent) => {
                child.props.onClick?.(e);
                setIsOpen(false);
            },
        });
    }

    return (
        <button
            className={ className }
            onClick={ () => {
                return setIsOpen(false);
            } }
            ref={ ref }
            type='button'
            { ...props }
        >
            {children}
        </button>
    );
});

SheetClose.displayName = 'SheetClose';

type SheetSide = 'top' | 'bottom' | 'left' | 'right';

const sheetVariants = cva(
    'fixed z-50 gap-4 bg-background p-6 shadow-lg transition-transform duration-300 ease-in-out',
    {
        variants: {
            side: {
                top: 'inset-x-0 top-0 border-b',
                bottom: 'inset-x-0 bottom-0 border-t',
                left: 'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
                right: 'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
            },
        },
        defaultVariants: {
            side: 'right',
        },
    }
);

const getSlideClasses = (side: SheetSide) => {
    const slideMap: Record<SheetSide, { from: string; to: string }> = {
        top: { from: '-translate-y-full', to: 'translate-y-0' },
        bottom: { from: 'translate-y-full', to: 'translate-y-0' },
        left: { from: '-translate-x-full', to: 'translate-x-0' },
        right: { from: 'translate-x-full', to: 'translate-x-0' },
    };

    return slideMap[side] || slideMap.right;
};

interface SheetContentProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
    side?: SheetSide;
}

const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(({ side = 'right', className, children, ...props }, ref) => {
    const { isOpen, setIsOpen } = useSheetContext();
    const slideClasses = getSlideClasses(side);

    return (
        <Transition as={ Fragment } show={ isOpen }>
            <Dialog as='div' className='relative z-50' onClose={ () => {
                return setIsOpen(false);
            } } { ...props }>
                <TransitionChild
                    as={ Fragment }
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div className='fixed inset-0 bg-black/80' />
                </TransitionChild>

                <div className='fixed inset-0 overflow-hidden'>
                    <TransitionChild
                        as={ Fragment }
                        enter='ease-out duration-300'
                        enterFrom={ slideClasses.from }
                        enterTo={ slideClasses.to }
                        leave='ease-in duration-200'
                        leaveFrom={ slideClasses.to }
                        leaveTo={ slideClasses.from }
                    >
                        <DialogPanel
                            className={ clsx(sheetVariants({ side }), className) }
                            ref={ ref }
                        >
                            {children}
                            <button
                                className='absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                                onClick={ () => {
                                    return setIsOpen(false);
                                } }
                                type='button'
                            >
                                <XMarkIcon className='h-4 w-4' />
                                <span className='sr-only'>Close</span>
                            </button>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
});

SheetContent.displayName = 'SheetContent';

const SheetHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div className={ clsx('flex flex-col space-y-2 text-center sm:text-left', className) } { ...props } />
    );
};

SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
    return (
        <div className={ clsx('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className) } { ...props } />
    );
};

SheetFooter.displayName = 'SheetFooter';

const SheetTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => {
    return (
        <DialogTitle className={ clsx('text-lg font-semibold text-foreground', className) } ref={ ref } { ...props } />
    );
});

SheetTitle.displayName = 'SheetTitle';

const SheetDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => {
    return (
        <Description className={ clsx('text-sm text-muted-foreground', className) } ref={ ref } { ...props } />
    );
});

SheetDescription.displayName = 'SheetDescription';

// Compatibility exports (no-ops for API compatibility)
const SheetPortal = ({ children }: { children?: ReactNode }) => {
    return children;
};
const SheetOverlay = () => {
    return null;
};

export {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetOverlay,
    SheetPortal,
    SheetTitle,
    SheetTrigger
};
